import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from './queryKeys';
import {
  getDocumentTemplates,
  getEmailTemplates,
  getDocumentTemplate,
  getEmailTemplate,
  uploadDocumentTemplate,
  createEmailTemplate,
  createHtmlDocumentTemplate,
  updateDocumentTemplate,
  updateEmailTemplate,
  deleteDocumentTemplate,
  setDefaultDocumentTemplate,
  deactivateDocumentTemplate,
  getDocumentTemplatePreviewHtml,
  deactivateEmailTemplate,
  setDefaultEmailTemplate,
  deleteEmailTemplate,
} from '@/services/templateService';
import type {
  DocumentTemplateCreateHTML,
  DocumentTemplateUpdate,
} from '@/types/template';

interface UploadDocumentTemplateData {
  file: File;
  name: string;
  description?: string;
  template_type: string;
  sample_data?: Record<string, unknown>;
}

interface CreateEmailTemplateData {
  name: string;
  description?: string;
  template_type: string;
  subject_template: string;
  html_template: string;
  text_template?: string;
}

// --- Document Templates ---

export function useDocumentTemplates() {
  const queryClient = useQueryClient();

  const { data: documentTemplates, isLoading: isLoadingDocuments } = useQuery({
    queryKey: queryKeys.templates.documents(),
    queryFn: () => getDocumentTemplates(),
  });

  const uploadDocumentMutation = useMutation({
    mutationFn: (data: UploadDocumentTemplateData) =>
      uploadDocumentTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.templates.documents(),
      });
    },
  });

  const createHtmlDocumentMutation = useMutation({
    mutationFn: (data: DocumentTemplateCreateHTML) =>
      createHtmlDocumentTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.templates.documents(),
      });
    },
  });

  const updateDocumentMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: DocumentTemplateUpdate }) =>
      updateDocumentTemplate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.templates.documents(),
      });
    },
  });

  const setDefaultDocumentMutation = useMutation({
    mutationFn: (id: string) => setDefaultDocumentTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.templates.documents(),
      });
    },
  });

  const deactivateDocumentMutation = useMutation({
    mutationFn: (id: string) => deactivateDocumentTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.templates.documents(),
      });
    },
  });

  const deleteDocumentMutation = useMutation({
    mutationFn: (id: string) => deleteDocumentTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.templates.documents(),
      });
    },
  });

  const previewHtmlDocumentMutation = useMutation({
    mutationFn: (id: string) => getDocumentTemplatePreviewHtml(id),
  });

  return {
    documentTemplates,
    isLoadingDocuments,
    uploadDocumentTemplate: uploadDocumentMutation.mutateAsync,
    createHtmlDocumentTemplate: createHtmlDocumentMutation.mutateAsync,
    updateDocumentTemplate: updateDocumentMutation.mutateAsync,
    setDefaultDocumentTemplate: setDefaultDocumentMutation.mutateAsync,
    deactivateDocumentTemplate: deactivateDocumentMutation.mutateAsync,
    deleteDocumentTemplate: deleteDocumentMutation.mutateAsync,
    previewHtmlDocumentTemplate: previewHtmlDocumentMutation.mutateAsync,
    isUploadingDocument: uploadDocumentMutation.isPending,
    isCreatingHtmlDocument: createHtmlDocumentMutation.isPending,
    isUpdatingDocument: updateDocumentMutation.isPending,
    isSettingDefaultDocument: setDefaultDocumentMutation.isPending,
    isDeactivatingDocument: deactivateDocumentMutation.isPending,
    isDeletingDocument: deleteDocumentMutation.isPending,
    isPreviewingHtmlDocument: previewHtmlDocumentMutation.isPending,
  };
}

// --- Email Templates ---

export function useEmailTemplates() {
  const queryClient = useQueryClient();

  const { data: emailTemplates, isLoading: isLoadingEmails } = useQuery({
    queryKey: queryKeys.templates.emails(),
    queryFn: getEmailTemplates,
  });

  const createEmailMutation = useMutation({
    mutationFn: (data: CreateEmailTemplateData) => createEmailTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.templates.emails() });
    },
  });

  const updateEmailMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CreateEmailTemplateData> & { is_active?: boolean };
    }) => updateEmailTemplate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.templates.emails() });
    },
  });

  const deactivateEmailMutation = useMutation({
    mutationFn: (id: string) => deactivateEmailTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.templates.emails() });
    },
  });

  const setDefaultEmailMutation = useMutation({
    mutationFn: (id: string) => setDefaultEmailTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.templates.emails() });
    },
  });

  const deleteEmailMutation = useMutation({
    mutationFn: (id: string) => deleteEmailTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.templates.emails() });
    },
  });

  return {
    emailTemplates,
    isLoadingEmails,
    createEmailTemplate: createEmailMutation.mutateAsync,
    updateEmailTemplate: updateEmailMutation.mutateAsync,
    deactivateEmailTemplate: deactivateEmailMutation.mutateAsync,
    setDefaultEmailTemplate: setDefaultEmailMutation.mutateAsync,
    deleteEmailTemplate: deleteEmailMutation.mutateAsync,
    isCreatingEmail: createEmailMutation.isPending,
    isUpdatingEmail: updateEmailMutation.isPending,
    isDeactivatingEmail: deactivateEmailMutation.isPending,
    isSettingDefaultEmail: setDefaultEmailMutation.isPending,
    isDeletingEmail: deleteEmailMutation.isPending,
  };
}

// --- Individual template detail hooks ---

export function useDocumentTemplate(templateId: string) {
  return useQuery({
    queryKey: queryKeys.templates.document(templateId),
    queryFn: () => getDocumentTemplate(templateId),
    enabled: !!templateId,
  });
}

export function useEmailTemplate(templateId: string) {
  return useQuery({
    queryKey: queryKeys.templates.email(templateId),
    queryFn: () => getEmailTemplate(templateId),
    enabled: !!templateId,
  });
}
